<template>
  <TheBaseApp v-if="isAppReady">
    <v-overlay v-model="overlay" contained />
    <TheDrawer
      :drawer-state="drawerState"
      @updateDrawerState="updateDrawerState($event)"
    />
    <v-main>
      <TheAppBar
        :drawer-state="drawerState"
        @updateDrawerState="updateDrawerState($event)"
      />
      <div class="page_wrapper">
        <router-view :key="$route.fullPath" />
      </div>
    </v-main>
    <TheSnackbar />
    <div v-if="showInactivityWarning" class="inativity_warning">
      <div class="left_section">
        <BaseIcon icon="$mdiAlertDecagramOutline" color="white" />
      </div>
      <div class="middle_section">
        <div class="section_title" v-text="$t('inactivity_warning.title')" />
        <div
          class="section_sub_title"
          v-text="$t('inactivity_warning.sub_title')"
        />
        <div class="message" v-text="$t('inactivity_warning.message')" />
      </div>
      <div class="right_section">
        <BaseButton
          icon="$mdiPlay"
          class="h_action"
          small
          @click="showInactivityWarning = false"
        >
          <span v-text="$t('inactivity_warning.action')" />
        </BaseButton>
      </div>
    </div>
  </TheBaseApp>
</template>

<script>
import TheBaseApp from "@/core/Components/TheBaseApp";
import TheDrawer from "@/core/Components/TheDrawer";
import TheAppBar from "@/core/Components/TheAppBar";
import TheSnackbar from "@/core/Components/TheSnackbar";
import Oauth from "@/core/Models/Oauth";
import { setBearerToken } from "@/core/Api/cosmoAxiosInstance";
import { AutoLogoutService } from "@/core/Services/AutoLogoutService";
import { mapState } from "vuex";

export default {
  name: "BackofficeLayout",
  components: {
    TheBaseApp,
    TheDrawer,
    TheAppBar,
    TheSnackbar,
  },
  data() {
    return {
      isAppReady: false,
      drawerState: true,
      autoLogoutService: null,
      showInactivityWarning: false,
    };
  },
  computed: {
    ...mapState({
      hasLoggedNormaly: (state) => state.core.hasLoggedNormaly,
      overlay: (state) => state.snackbar.overlay,
    }),
    organizationId() {
      return this.$route.params.organizationId;
    },
  },
  watch: {
    organizationId: {
      handler() {
        this.updateActiveOrganization();
      },
      immediate: true,
    },
  },
  created() {
    this.reset();
    this.initialize();
    this.inittDrawerState();
    this.initAutoLogoutService();
  },
  destroyed() {
    this.reset();
    this.autoLogoutService.stop();
  },
  methods: {
    reset() {
      this.$store.commit("organization/setActiveOrganization", null);
      this.$store.commit("organization/setOrganizations", []);
    },
    async initialize() {
      this.isAppReady = false;
      if (Oauth.localStorage && !this.hasLoggedNormaly) {
        // if oauth exist we refresh the token.
        const oauth = Oauth.localStorage;
        try {
          setBearerToken(oauth.accessToken);
        } catch (error) {
          // if its not valid we redirect to login
          this.$store.dispatch("core/resetApp");
          return this.$router.push({
            name: "Login",
            params: { next: this.$route.fullPath },
          });
        }
      }
      if (this.user == null) {
        await this.$store.dispatch("account/fetchUser");
      }
      await this.$store.dispatch("organization/fetchOrganizations");
      this.updateActiveOrganization();
      this.isAppReady = true;
    },
    inittDrawerState() {
      const tmp = localStorage.getItem("drawerState");
      this.drawerState = !tmp || tmp == "false" ? false : true;
    },
    updateDrawerState(state) {
      this.drawerState = state;
      localStorage.setItem("drawerState", this.drawerState);
    },
    initAutoLogoutService() {
      this.autoLogoutService = new AutoLogoutService({
        onInactive: this.displayInactiveWarning,
        onTimeout: this.logoutUser,
      });
      this.autoLogoutService.start();
    },
    displayInactiveWarning() {
      this.showInactivityWarning = true;
    },
    logoutUser() {
      localStorage.removeItem("oauth");
      localStorage.removeItem("oauthAdmin");
      localStorage.removeItem("organizationsFilters");
      localStorage.setItem("autoLoggedOut", "true");
      this.$router.push("/auth/login/");
    },
    updateActiveOrganization() {
      let organizationId = this.$route.params.organizationId;
      if (!organizationId) {
        this.$store.commit("organization/setActiveOrganization", null);
      } else
        this.$store.dispatch(
          "organization/setActiveOrganizationById",
          organizationId
        );
    },
  },
};
</script>

<style lang="scss" src="@/core/Assets/scss/typography.scss"></style>

<style lang="scss" scoped>
.page_wrapper {
  height: calc(100vh - 72px);
  overflow: auto;
  margin: 8px;
  padding: 32px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #fff;
    border-radius: 3px;
    border: 1px solid #eeeef7;
  }

  &::-webkit-scrollbar-thumb {
    background: #b6bdff;
    border-radius: 3px;
  }
}
.inativity_warning {
  position: fixed;
  z-index: 10000000;
  top: 20px;
  left: 20px;
  width: calc(100vw - 40px);
  display: flex;
  padding: 16px;
  border-radius: 5px;
  background: #fff;
  gap: 16px;
  margin-bottom: 24px;
  &::after {
    content: "";
    position: absolute;
    z-index: -2;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(#fc001d, 0.1);
  }
  .left_section {
    flex: none;
    width: 32px;
    height: 32px;
    border-radius: 5px;
    background: #fc001d;
    display: flex;
    justify-content: center;
    align-items: center;
    ::v-deep {
      .v-icon {
        width: 20px;
        height: 20px;
      }
    }
  }
  .middle_section {
    flex: 1;
    font-size: 14px;
    line-height: 20px;
    .section_title {
      color: #000;
      font-size: 16px;
      line-height: 22px;
    }
    .section_sub_title {
      color: #353542;
    }
    .message {
      color: #707080;
    }
  }
}
</style>

<style>
.inset-0 {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
}
.top {
  top: 0;
}
.right {
  right: 0;
}
.bottom {
  bottom: 0;
}
.relative {
  position: relative;
}
.absolute {
  position: absolute;
}
</style>
